import merge from "lodash-es/merge";
import {
    deleteAccessoryFailure,
    deleteAccessoryRequest,
    deleteAccessorySuccess,
    getAccessoriesFailure,
    getAccessoriesRequest,
    getAccessoriesSuccess,
    postAccessoryFailure,
    postAccessoryRequest,
    postAccessorySuccess,
} from "src/store/actions/accessories";
import { getDealSuccess } from "src/store/actions/deal";
import { pollingSuccess } from "src/store/actions/poller";
import { ActionType } from "src/store/dataService/actions/utils/actionCreator";
import { Accessory } from "Types/api/Accessory";
import { AccessoryPayments } from "Types/api/AccessoryPayments";
import {
    getAccessoriesPaymentsFailure,
    getAccessoriesPaymentsRequest,
    getAccessoriesPaymentsSuccess,
} from "src/store/actions/accessoriesPayments";
import { patchPaymentsRequest } from "src/store/actions/payments";
import { patchConditionalRebatesHandRaisersSuccess } from "src/store/actions/conditionalRebates";
import { patchTradeInSuccess } from "src/store/actions/tradeIn";
import { DealAction } from "src/store/dataService/actions/deal";
import { API } from "src/store/dataService/types";

export interface AccessoriesState {
    [vin: string]: {
        entities?: {
            [id: number]: Accessory & AccessoryPayments;
        };
        available?: {
            loan?: number[];
            lease?: number[];
            cash?: number[];
        };
        selected?: number[];
        updating?: number[];
        isLoading?: boolean;
        shouldFetchPayments?: boolean;
    };
}

type AccessoryActions =
    | ActionType<typeof getAccessoriesRequest>
    | ActionType<typeof getAccessoriesSuccess>
    | ActionType<typeof getAccessoriesFailure>
    | ActionType<typeof postAccessoryRequest>
    | ActionType<typeof postAccessorySuccess>
    | ActionType<typeof postAccessoryFailure>
    | ActionType<typeof deleteAccessoryRequest>
    | ActionType<typeof deleteAccessorySuccess>
    | ActionType<typeof deleteAccessoryFailure>
    | ActionType<typeof getAccessoriesPaymentsRequest>
    | ActionType<typeof getAccessoriesPaymentsSuccess>
    | ActionType<typeof getAccessoriesPaymentsFailure>
    | ActionType<typeof getDealSuccess>
    | ActionType<typeof patchPaymentsRequest>
    | ActionType<typeof patchConditionalRebatesHandRaisersSuccess>
    | ActionType<typeof patchTradeInSuccess>
    | DealAction
    | ActionType<typeof getDealSuccess>
    | ActionType<typeof pollingSuccess>;

const accessories = (state: AccessoriesState = {}, action: AccessoryActions): AccessoriesState => {
    switch (action.type) {
        case getAccessoriesRequest.type:
            return {
                ...state,
                [action.payload.vin]: {
                    ...state[action.payload.vin],
                    entities: Object.entries(state[action.payload.vin].entities ?? {}).reduce(
                        (prev, [accessoryId, accessory]) => ({
                            ...prev,
                            [accessoryId]: {
                                ...accessory,
                            },
                        }),
                        {}
                    ),
                    isLoading: true,
                },
            };
        case getAccessoriesSuccess.type:
            return {
                ...state,
                [action.payload.vin]: {
                    ...state[action.payload.vin],
                    entities: merge(
                        {},
                        state[action.payload.vin]?.entities,
                        action.payload.accessories.reduce(
                            (previousValue, currentValue) => ({ ...previousValue, [currentValue.id]: currentValue }),
                            {}
                        )
                    ),
                    available: {
                        ...state[action.payload.vin]?.available,
                        [action.payload.paymentType]: action.payload.accessories.map((accessory) => accessory.id),
                    },
                    isLoading: false,
                },
            };
        case getAccessoriesFailure.type:
            return {
                ...state,
                [action.payload.vin]: {
                    ...state[action.payload.vin],
                    isLoading: false,
                },
            };
        case postAccessoryRequest.type:
            return {
                ...state,
                [action.payload.vin]: {
                    ...state[action.payload.vin],
                    selected: [...(state[action.payload.vin]?.selected ?? []), action.payload.accessoryId],
                    updating: [...(state[action.payload.vin]?.updating ?? []), action.payload.accessoryId],
                },
            };
        case postAccessorySuccess.type:
            return {
                ...state,
                [action.payload.vin]: {
                    ...state[action.payload.vin],
                    updating: state[action.payload.vin]?.updating?.filter((id) => id !== action.payload.accessoryId),
                },
            };
        case postAccessoryFailure.type:
            return {
                ...state,
                [action.payload.vin]: {
                    ...state[action.payload.vin],
                    selected: state[action.payload.vin]?.selected?.filter((id) => id !== action.payload.accessoryId),
                    updating: state[action.payload.vin]?.updating?.filter((id) => id !== action.payload.accessoryId),
                },
            };
        case deleteAccessoryRequest.type:
            return {
                ...state,
                [action.payload.vin]: {
                    ...state[action.payload.vin],
                    selected: state[action.payload.vin]?.selected?.filter((id) => id !== action.payload.accessoryId),
                    updating: [...(state[action.payload.vin]?.updating ?? []), action.payload.accessoryId],
                },
            };
        case deleteAccessorySuccess.type:
            return {
                ...state,
                [action.payload.vin]: {
                    ...state[action.payload.vin],
                    updating: state[action.payload.vin]?.updating?.filter((id) => id !== action.payload.accessoryId),
                },
            };
        case deleteAccessoryFailure.type:
            return {
                ...state,
                [action.payload.vin]: {
                    ...state[action.payload.vin],
                    selected: [...(state[action.payload.vin]?.selected ?? []), action.payload.accessoryId],
                    updating: state[action.payload.vin]?.updating?.filter((id) => id !== action.payload.accessoryId),
                },
            };
        case getAccessoriesPaymentsRequest.type:
            return {
                ...state,
                [action.payload.vin]: {
                    ...state[action.payload.vin],
                    shouldFetchPayments: false,
                    entities: Object.entries(state[action.payload.vin].entities ?? {}).reduce(
                        (prev, [accessoryId, accessory]) => ({
                            ...prev,
                            [accessoryId]: {
                                ...accessory,
                                lease: null,
                                loan: null,
                            },
                        }),
                        {}
                    ),
                },
            };

        case getAccessoriesPaymentsSuccess.type:
            if (state[action.payload.vin]?.shouldFetchPayments === true) {
                return state;
            }

            return {
                ...state,
                [action.payload.vin]: {
                    ...state[action.payload.vin],
                    entities: merge(
                        {},
                        state[action.payload.vin]?.entities,
                        action.payload.payments.reduce(
                            (previousValue, currentValue) => ({ ...previousValue, [currentValue.id]: currentValue }),
                            {}
                        )
                    ),
                },
            };

        case patchPaymentsRequest.type:
            // We fetch accessory payments for both payments types at the same time
            // So if all we changed is payment type, we don't need to refetch
            if (
                Object.keys(action.payload.payments).length === 1 &&
                Object.keys(action.payload.payments)?.[0] === "selected"
            ) {
                return state;
            }

            return {
                ...state,
                [action.payload.vin]: {
                    ...state?.[action.payload.vin],
                    shouldFetchPayments: true,
                },
            };
        case patchTradeInSuccess.type:
        case patchConditionalRebatesHandRaisersSuccess.type:
        case API.Deal:
            return {
                ...Object.entries(state).reduce(
                    (newState, [vin, vinState]) => ({
                        ...newState,
                        [vin]: { ...vinState, shouldFetch: true },
                    }),
                    {}
                ),
            };

        case getDealSuccess.type:
        case pollingSuccess.type:
            return merge(
                {},
                state,
                action.payload.deal.vehicles?.reduce(
                    (prev, curr) => ({
                        ...prev,
                        [curr.vin]: {
                            entities: curr.accessories?.reduce((prev, curr) => ({ ...prev, [curr.id]: curr }), {}),
                            selected: [
                                ...(curr.accessories
                                    ?.map((accessory) => accessory.id)
                                    .filter((accessoryId) => !state[curr.vin]?.updating?.includes(accessoryId)) ?? []),
                                ...(state[curr.vin]?.selected?.filter((accessoryId) =>
                                    state[curr.vin]?.updating?.includes(accessoryId)
                                ) ?? []),
                            ],
                            shouldFetchPayments: action.payload.deal.dealer_changes?.filter((change) =>
                                change.path.includes(curr.vin)
                            )?.length
                                ? true
                                : state?.[curr.vin]?.shouldFetchPayments ?? true,
                        },
                    }),
                    {}
                )
            );
        default:
            return state;
    }
};

export default accessories;
